<script lang="ts" setup>
import { useSlots } from 'vue'
import {
  Dialog,
  DialogDescription,
  DialogOverlay,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { PongoButton } from '@heypongo/brick'
import { XMarkIcon } from '@heroicons/vue/24/solid'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    btn?: {
      text: string
      bgColor: string
      textColor: string
      isProcessing?: boolean
      link?: string
      click?: () => void
    } | null
    cancelBtn?: {
      text: string
      bgColor: string
      textColor: string
    } | null
    hasBlur?: boolean
    hasCloseIcon?: boolean
    canClose?: boolean
  }>(),
  {
    cancelBtn: () => ({
      text: '',
      bgColor: '',
      textColor: '',
    }),
    btn: () => ({
      text: '',
      bgColor: '',
      textColor: '',
      link: '',
      isProcessing: false,
      click: () => void 0,
    }),
    hasCloseIcon: false,
    hasBlur: true,
    canClose: true,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', isOpen: boolean): void
  (e: 'onBtnClick'): void
  (e: 'onCancelBtnClick'): void
}>()

const slots = useSlots()

const closeModal = () => {
  if (props.canClose) {
    emit('update:modelValue', false)
  }
}

const onClickCancelBtn = () => {
  emit('update:modelValue', false)
}

const onClickBtn = () => {
  if (props.btn?.click) {
    props.btn.click()
  } else {
    emit('onBtnClick')
  }
}
</script>

<template>
  <div v-if="modelValue">
    <Teleport to="#app">
      <TransitionRoot :show="modelValue" as="template">
        <Dialog as="div" class="relative z-10" @close="closeModal">
          <DialogOverlay
            class="fixed inset-0 z-20"
            :class="hasBlur && 'bg-black/60 backdrop-blur'"
            aria-hidden="true"
            tabindex="-1"
          />
          <TransitionChild
            enter="transform transition ease-in-out duration-700"
            enter-from="translate-y-full"
            enter-to="translate-y-0"
            leave="transform transition ease-in-out duration-500"
            leave-from="translate-y-0"
            leave-to="translate-y-full"
            as="template"
          >
            <DialogPanel
              class="fixed bottom-0 left-0 right-0 z-40 h-auto w-full rounded-t-4xl border-t border-gray-100 bg-white text-xl shadow lg:mx-auto lg:max-w-4xl"
            >
              <div
                v-if="hasCloseIcon"
                class="absolute right-0 top-0 m-6 cursor-pointer"
                @click="closeModal"
              >
                <XMarkIcon class="aspect-square w-5" />
              </div>
              <DialogTitle
                class="px-6 text-left text-2xl font-bold text-gray-800"
                :class="slots.title || slots.subtitle ? 'py-7' : 'pt-7'"
              >
                <slot name="title" />
                <slot name="subtitle" />
              </DialogTitle>
              <DialogDescription as="div">
                <div
                  :class="[
                    'flex items-center justify-start px-6 pb-7 text-left text-gray-800',
                    {
                      'mb-20':
                        slots.description && (btn?.text?.length > 0 || cancelBtn?.text?.length > 0),
                      'mb-[3rem]': !slots.description,
                    },
                  ]"
                >
                  <slot name="description" />
                </div>
                <slot name="footer">
                  <div
                    class="fixed bottom-0 flex w-full items-center px-6 py-6 lg:mx-auto lg:max-w-4xl"
                  >
                    <div class="flex w-full items-center gap-4">
                      <PongoButton
                        v-if="cancelBtn?.text"
                        class="w-full py-4 font-semibold focus:ring-transparent"
                        rounded-size="rounded-xl"
                        :style="{
                          color: cancelBtn.textColor,
                          backgroundColor: '#FFFFFF',
                          borderWidth: '1px',
                          borderColor: cancelBtn.textColor,
                        }"
                        @click="onClickCancelBtn"
                        @touchend="onClickCancelBtn"
                      >
                        <span class="text-base"> {{ cancelBtn?.text }} </span>
                      </PongoButton>
                      <PongoButton
                        v-else-if="btn?.text"
                        class="w-full py-4 font-semibold focus:ring-transparent"
                        rounded-size="rounded-xl"
                        :processing="btn.isProcessing"
                        :style="{
                          backgroundColor: btn.bgColor,
                          color: btn.textColor,
                          borderColor: btn.bgColor,
                        }"
                        v-bind="btn.link ? { href: btn.link } : {}"
                        @click="onClickBtn"
                      >
                        <span class="text-base"> {{ btn?.text }} </span>
                      </PongoButton>
                    </div>
                  </div>
                </slot>
              </DialogDescription>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </TransitionRoot>
    </Teleport>
  </div>
</template>
